import React from 'react';

import { Box, CircularProgress, Typography, Tooltip as MuiTooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const useStyles = makeStyles(() => ({
  chartContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
  },
  chartTitle: {
    alignItems: 'center',
    borderBottom: '1px solid #eaeaea',
    display: 'flex',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: '20px',
    paddingBottom: '10px',
  },
  emptyState: {
    alignItems: 'center',
    color: '#999',
    display: 'flex',
    fontSize: 14,
    height: 400,
    justifyContent: 'center',
  },
  errorState: {
    alignItems: 'center',
    color: '#d32f2f',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    gap: 10,
    height: 400,
    justifyContent: 'center',
  },
  legendItem: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 12,
    gap: 8,
    marginBottom: 8,
  },
  legendSquare: {
    borderRadius: 2,
    height: 12,
    width: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    height: 400,
    justifyContent: 'center',
  },
  statItem: {
    textAlign: 'center',
  },
  statLabel: {
    color: '#666',
    fontSize: 12,
    marginBottom: 4,
  },
  statValue: {
    color: '#172B4D',
    fontSize: 20,
    fontWeight: 600,
  },
  statsContainer: {
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    gap: 30,
    justifyContent: 'center',
    marginTop: 20,
    paddingTop: 15,
  },
}));

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#FF6B9D',
  '#C23531',
  '#2F4554',
  '#61A0A8',
  '#D48265',
  '#91C7AE',
  '#749F83',
  '#CA8622',
  '#BDA29A',
  '#6E7074',
];

const FacilityComparisonChart = ({ data, endDate, error, loading, startDate }) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Facility Comparison by Modality - Observations per FTE</Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Facility Comparison by Modality - Observations per FTE</Typography>
        </Box>
        <Box className={classes.errorState}>
          <Typography>Failed to load facility comparison data</Typography>
          <Typography variant="caption">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Facility Comparison by Modality - Observations per FTE</Typography>
        </Box>
        <Box className={classes.emptyState}>
          <Typography>No facility data found for this period</Typography>
        </Box>
      </Box>
    );
  }

  // Sort data by observations per FTE (descending)
  const sortedData = [...data.data].sort((a, b) => b.observationsPerFTE - a.observationsPerFTE);

  const chartData = sortedData.map((item) => ({
    facilityId: item.facilityId,
    name: item.name,
    observationCount: item.observationCount,
    observationsPerFTE: item.observationsPerFTE,
    technologistCount: item.technologistCount,
    totalFTE: item.totalFTE,
  }));

  const totalObservations = chartData.reduce((sum, item) => sum + item.observationCount, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
          }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{data.name}</Typography>
          <Typography style={{ fontSize: 11 }}>Total Observations: {data.observationCount}</Typography>
          <Typography style={{ fontSize: 11 }}>Total FTE: {data.totalFTE}</Typography>
          <Typography style={{ fontSize: 11 }}>Technologists: {data.technologistCount}</Typography>
          <Typography style={{ color: '#2196f3', fontSize: 11, fontWeight: 600 }}>Observations per FTE: {data.observationsPerFTE}</Typography>
        </Box>
      );
    }
    return null;
  };

  // Fixed height for simple vertical bar chart
  const chartHeight = 400;

  const renderLegendBelow = () => {
    return (
      <Box display="flex" flexDirection="column" flexWrap="wrap" marginTop="20px" paddingTop="15px" borderTop="1px solid #eaeaea">
        {chartData.map((item, index) => {
          return (
            <Box key={item.facilityId} className={classes.legendItem}>
              <Box className={classes.legendSquare} style={{ backgroundColor: COLORS[index % COLORS.length] }} />
              <Typography>
                {item.name} ({item.observationsPerFTE})
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.chartTitle}>
        <Typography>
          Facility Comparison by Modality
          {startDate && endDate && (
            <Typography component="span" variant="caption" style={{ color: '#666', fontWeight: 400, marginLeft: 8 }}>
              ({startDate} - {endDate})
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Vertical Bar Chart with Colors */}
      <Box style={{ height: chartHeight, marginTop: 20 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            margin={{
              bottom: 20,
              left: 20,
              right: 20,
              top: 20,
            }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={false} height={30} />
            <YAxis label={{ angle: -90, style: { fontFamily: 'Roboto', fontSize: '14px' }, value: '# of observations / FTE' }} style={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
            <Bar dataKey="observationsPerFTE">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${entry.facilityId}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Note about filtered facilities */}
      <Box style={{ marginBottom: 10 }}>
        <Typography variant="caption" style={{ color: '#999', fontSize: 11, fontStyle: 'italic' }}>
          * Facilities without technologists are not shown
        </Typography>
      </Box>

      {/* Legend Below */}
      {renderLegendBelow()}

      {/* Stats Section */}
      <Box className={classes.statsContainer}>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Total Facilities</Typography>
          <Typography className={classes.statValue}>{chartData.length}</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Total Observations</Typography>
          <Typography className={classes.statValue}>{totalObservations}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FacilityComparisonChart;
